import { env } from '@/env';
import { isAuthFailure } from '@/shared/auth-failure';
import { getAuthToken, redirectToLogin } from '@/shared/auth-token';
import { reportStorageModuleRefusal } from '@/shared/storage-module';

/**
 * The HTTP transport to `nexvia-api-web` — predicted (and named) by this repo's own AGENTS.md
 * ("Add it when the first real request is needed, reading VITE_API_URL from env.ts"). Milestone 5
 * (Solicitudes y Mis cotizaciones) is that first request.
 *
 * Bearer-token scheme, NOT nexvia-backoffice's `credentials: 'include'` cookie scheme: every
 * request asks `@/shared/auth-token`'s `getAuthToken()` for the live Firebase ID token and sends
 * it as `Authorization: Bearer <token>` — the same mechanism nexvia-web-app uses against the same
 * customer-facing API surface, not the admin one.
 *
 * **Fixed 2026-10-14**: this used to read a cached copy straight out of
 * `localStorage[TOKEN_STORAGE_KEY]`, which only gets refreshed by `useAuth.tsx`'s
 * `onIdTokenChanged` listener — a listener that depends on Firebase's own background timer having
 * fired recently, which it does not while a tab is backgrounded or the machine is asleep. That
 * sent repeated expired tokens to `/storage/*`, each answered `403` (nexvia-api-web's status for
 * an expired Firebase token on these routes — not 401), with nothing reacting to it. Now asks the
 * live SDK instead (see `auth-token.ts`'s own docblock for why that alone fixes it), and retries
 * once with a forced refresh on a 401 *or* 403 before redirecting to `/login` — see `request()`
 * below.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export type QueryParams = Record<string, string | number | boolean | undefined | null>;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  params?: QueryParams;
}

function buildUrl(path: string, params?: QueryParams): string {
  const url = new URL(path, env.VITE_API_URL);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Narrows the parsed body enough to read `message`/`code` without assuming the rest of its shape. */
function readStringField(payload: unknown, field: 'message' | 'code'): string | undefined {
  if (payload && typeof payload === 'object' && field in payload) {
    const value = (payload as Record<string, unknown>)[field];
    return typeof value === 'string' ? value : undefined;
  }
  return undefined;
}

interface RawResponse {
  ok: boolean;
  status: number;
  statusText: string;
  payload: unknown;
}

async function sendRequest(path: string, { method = 'GET', body, params }: RequestOptions, token: string | null): Promise<RawResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(buildUrl(path, params), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined
  });

  const text = await response.text();
  const payload = text ? safeParseJson(text) : null;
  return { ok: response.ok, status: response.status, statusText: response.statusText, payload };
}

// error.middleware.ts in nexvia-api-web always answers `{ success: false, message, code? }` for an
// operational error; a non-JSON body (a gateway timeout's HTML page, for example) falls back to
// the status line instead of throwing on JSON.parse.
function toApiError(raw: RawResponse): ApiError {
  const message = readStringField(raw.payload, 'message') ?? `Error ${raw.status} ${raw.statusText}`.trim();
  return new ApiError(message, raw.status, readStringField(raw.payload, 'code'));
}


/**
 * One retry, ever, per call — never a loop: a 401/403 forces a live token refresh
 * (`getAuthToken(true)`) and replays the exact same request once with whatever token that
 * returns; anything still wrong after that single replay (no refreshed token to retry with, or
 * the replay is *also* 401/403) redirects to `/login` instead of trying again, and surfaces the
 * same `ApiError` shape this module has always thrown — only the retry-then-redirect behavior
 * around it is new. A non-auth failure (validation, 5xx, …) is never retried.
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = await getAuthToken();
  let raw = await sendRequest(path, options, token);

  // NEX-1164 — only a session failure (401, 403 AUTH_*) retries/redirects; a 403 domain refusal

  // (STORAGE_NOT_APPROVED, STORAGE_ROLE_REQUIRED, ...) falls through to the page as a plain ApiError.

  if (isAuthFailure(raw.status, raw.payload)) {
    const refreshedToken = await getAuthToken(true);
    if (refreshedToken) {
      raw = await sendRequest(path, options, refreshedToken);
    }
    if (!refreshedToken || isAuthFailure(raw.status, raw.payload)) {
      redirectToLogin();
      throw toApiError(raw);
    }
  }

  if (!raw.ok) {
    const error = toApiError(raw);
    reportStorageModuleRefusal(error.status, error.code);
    throw error;
  }

  return (raw.payload ?? ({} as unknown)) as T;
}

export const api = {
  get: <T>(path: string, params?: QueryParams) => request<T>(path, { method: 'GET', params }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' })
};
